import {
  CSSProperties,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import "./KaiAgentStatus.scss";

/**
 * The Kai mark with working states, for conversations without a custom
 * operator image. Idle is the face (ring, eye, dash) with a blink; every
 * other state folds the three parts into a glyph and animates it there:
 * thinking (dots), fetching (orbit), tools (cog), working (bars), done
 * (check), attention (!), paused (‖). Changing `state` morphs straight from
 * one glyph to the next, so thinking → fetching → tools → done plays as one
 * continuous motion.
 *
 * Port of the dashboard's KaiAgentStatus, fixed to the Kai face. Same
 * drawing as GleapBotIcon.svg on a 100-unit grid. Glyph geometry is CSS
 * `x/y/width/height/rx` on rects, so a state change is a plain transition;
 * only the in-glyph loops are keyframes. The glyph classes are applied
 * imperatively with a freeze-and-release step: a CSS loop's value is dropped
 * the instant its class goes, so pinning each piece's current transform
 * inline, swapping the classes and releasing makes the transition start
 * from wherever the loop left it.
 */
export const KaiAgentStatus = ({
  state,
  size = 20,
  radius = "var(--border-radius-40)",
  className,
  ariaHidden = true,
  frozen = false,
  style,
}: KaiAgentStatusProps) => {
  const rootRef = useRef<HTMLSpanElement>(null);
  const [happy, setHappy] = useState(false);
  const previousState = useRef<KaiAgentState>(state);
  // Own blink offset per instance so a column of tiles never blinks in unison.
  const [blinkDelay] = useState(
    () => `${-(Math.random() * 7).toFixed(2)}s`,
  );

  useEffect(() => {
    const wasDone = previousState.current === "done";
    previousState.current = state;
    if (!wasDone || state !== "idle") {
      setHappy(false);
      return undefined;
    }
    setHappy(true);
    const timer = window.setTimeout(() => setHappy(false), HAPPY_MS);
    return () => window.clearTimeout(timer);
  }, [state]);

  const glyph = state === "idle" ? null : STATE_GLYPHS[state];

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || !root.isConnected) return undefined;
    const wasOn = root.classList.contains("kas--on");
    const movers = frozen
      ? []
      : Array.from(
          root.querySelectorAll<SVGElement>(
            ".kas__parts, .kas__shape, .kas__glyph",
          ),
        );
    // Pinning the value the loop is at right now equals the animated value,
    // so nothing moves yet; once the class swap removes the loop the pin is
    // what the release transitions from.
    movers.forEach((el) => {
      el.style.transform = frozenTransform(el);
    });
    root.classList.remove(...GLYPH_CLASSES);
    if (glyph) root.classList.add("kas--on", `kas--${glyph}`);
    root.classList.toggle("kas--happy", happy && !frozen);
    root.classList.toggle("kas--frozen", frozen);
    let enterTimer: number | undefined;
    if (glyph && !wasOn && !frozen) {
      // Leaving idle: the ring folds first, the pieces travel second.
      root.classList.add("kas--enter");
      enterTimer = window.setTimeout(
        () => root.classList.remove("kas--enter"),
        ENTER_MS,
      );
    }
    void root.offsetWidth; // flush, so the pins are where the transitions start
    movers.forEach((el) => {
      el.style.transform = "";
    });
    return () => window.clearTimeout(enterTimer);
  }, [glyph, happy, frozen, className]);

  return (
    <span
      ref={rootRef}
      className={`kas${className ? ` ${className}` : ""}`}
      style={
        {
          width: size,
          height: size,
          borderRadius: radius,
          backgroundColor: "var(--kai-agent-tile)",
          color: "var(--kai-agent-face)",
          "--kas-blink-delay": blinkDelay,
          ...style,
        } as CSSProperties
      }
      role={ariaHidden ? undefined : "img"}
      aria-label={ariaHidden ? undefined : STATE_LABELS[state]}
      aria-hidden={ariaHidden || undefined}
      data-state={state}
    >
      <svg
        className="kas__svg"
        viewBox="0 0 100 100"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
      >
        <g className="kas__parts">
          <g className="kas__part kas__part-ring">
            <circle
              className="kas__ring"
              cx="50"
              cy="50"
              r="30.4"
              pathLength="360"
            />
            <rect
              className="kas__shape kas__alt"
              x="73.55"
              y="43.15"
              width="13.7"
              height="13.7"
              rx="6.85"
            />
          </g>
          <g className="kas__part kas__part-eye">
            <rect
              className="kas__shape kas__blink"
              x="32.05"
              y="38.35"
              width="13.7"
              height="13.7"
              rx="6.85"
            />
          </g>
          <g className="kas__part kas__part-dash">
            <rect
              className="kas__shape"
              x="57.7"
              y="41.7"
              width="14.5"
              height="6.9"
              rx="1.7"
            />
          </g>
        </g>
      </svg>
    </span>
  );
};

/**
 * What a piece is doing right now, as an inline transform. The parts group
 * only ever rotates and scales, so it is unwound the short way round; the
 * shapes keep their matrix as is.
 */
const frozenTransform = (el: SVGElement): string => {
  if (typeof DOMMatrix === "undefined") return "";
  const current = getComputedStyle(el).transform;
  if (current === "none") return "";
  if (!el.classList.contains("kas__parts")) return current;
  const m = new DOMMatrix(current);
  const angle = (Math.atan2(m.b, m.a) * 180) / Math.PI;
  return `rotate(${angle}deg) scale(${Math.hypot(m.a, m.b)})`;
};

export type KaiAgentState =
  | "idle"
  | "thinking"
  | "fetching"
  | "tools"
  | "working"
  | "done"
  | "attention"
  | "paused";

export const KAI_AGENT_STATES: KaiAgentState[] = [
  "idle",
  "thinking",
  "fetching",
  "tools",
  "working",
  "done",
  "attention",
  "paused",
];

export const STATE_LABELS: Record<KaiAgentState, string> = {
  idle: "Kai",
  thinking: "Kai is thinking",
  fetching: "Kai is gathering information",
  tools: "Kai is using a tool",
  working: "Kai is writing",
  done: "Kai is done",
  attention: "Kai needs your input",
  paused: "Kai handed over",
};

type Glyph = "dots" | "orbit" | "tools" | "bars" | "check" | "bang" | "pause";

const STATE_GLYPHS: Record<Exclude<KaiAgentState, "idle">, Glyph> = {
  thinking: "dots",
  fetching: "orbit",
  tools: "tools",
  working: "bars",
  done: "check",
  attention: "bang",
  paused: "pause",
};

const GLYPH_CLASSES = [
  "kas--on",
  "kas--enter",
  "kas--dots",
  "kas--orbit",
  "kas--tools",
  "kas--bars",
  "kas--check",
  "kas--bang",
  "kas--pause",
];

const HAPPY_MS = 1600;
/** Both beats of the idle → glyph morph. */
const ENTER_MS = 800;

interface KaiAgentStatusProps {
  state: KaiAgentState;
  size?: number;
  radius?: number | string;
  className?: string;
  ariaHidden?: boolean;
  /** No loops, no morph transitions: the final pose of `state`, for VR snapshots. */
  frozen?: boolean;
  style?: CSSProperties;
}
